'use client';

import { Badge, Box, HStack, Icon, Spacer, Tabs, Text, VStack } from '@chakra-ui/react';
import { DragDropContext, Draggable, type DropResult, Droppable } from '@hello-pangea/dnd';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { AiOutlineBarChart } from 'react-icons/ai';
import { FaBullseye } from 'react-icons/fa';
import { LuGrid3X3 } from 'react-icons/lu';
import { MdDelete, MdDragIndicator, MdEdit } from 'react-icons/md';

import { CsrPlansService } from '@/client';
import { stringToColor } from '@/utils';

import { CommitmentAddDialog } from './CommitmentAddDialog';
import { CommitmentDeleteDialog } from './CommitmentDeleteDialog';
import { CommitmentActions, CommitmentGoals, CommitmentIndicators } from './CommitmentItems';
import { PillarAddDialog } from './PillarAddDialog';
import { PillarDeleteDialog } from './PillarDeleteDialog';
import { useKanbanMutations } from './useKanbanMutations';

export default function KanbanBoard({ editMode }: { editMode: boolean }) {
  const { slug } = useParams({ strict: false });

  const { data: csrPlan } = useQuery({
    queryFn: () =>
      CsrPlansService.readCsrPlan({
        companySlug: slug,
      }),
    queryKey: ['csrPlans'],
  });

  const { mutatePillar, mutateCommitment, mutateAction, mutateIndicator, mutateGoal } = useKanbanMutations(slug);

  const onDragEnd = (result: DropResult) => {
    const { draggableId, destination, source, type } = result;

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const baseData = { order: destination.index };
    const movedToAnotherParent = destination.droppableId !== source.droppableId;

    // Define a mutation map
    const mutationMap: Record<string, { mutate: (data: { id: string; data: any }) => void; parentKey?: string }> = {
      pillar: { mutate: mutatePillar.mutate },
      commitment: { mutate: mutateCommitment.mutate, parentKey: 'pillar_id' },
      action: { mutate: mutateAction.mutate, parentKey: 'commitment_id' },
      indicator: { mutate: mutateIndicator.mutate, parentKey: 'commitment_id' },
      goal: { mutate: mutateGoal.mutate, parentKey: 'commitment_id' },
    };

    const mutationEntry = mutationMap[type];

    if (!mutationEntry) return;

    const data = {
      ...baseData,
      ...(movedToAnotherParent &&
        mutationEntry.parentKey && {
          [mutationEntry.parentKey]: destination.droppableId,
        }),
    };

    return mutationEntry.mutate({ id: draggableId, data });
  };

  return (
    <Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board" type="pillar" direction="horizontal" isDropDisabled={!editMode}>
          {(provided) => (
            <HStack
              ref={provided.innerRef}
              {...provided.droppableProps}
              height="calc(100vh - 72px)"
              overflow="auto"
              width={'full'}
              gap={4}
              align="flex-start"
              wrap="nowrap"
              p={4}
            >
              {csrPlan?.pillars.map((pillar, index) => (
                <Draggable draggableId={pillar.id} index={index} key={pillar.id} isDragDisabled={!editMode}>
                  {(provided, snapshot) => (
                    <VStack
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      gap={2}
                      w="400px"
                      align="stretch"
                      borderRadius="md"
                      pt={4}
                      opacity={snapshot.isDragging ? 0.5 : 1}
                      bg={stringToColor(pillar.name ?? '') + '.100'}
                    >
                      <HStack {...provided.dragHandleProps} px={4}>
                        {editMode && (
                          <Icon size={'sm'}>
                            <MdDragIndicator />
                          </Icon>
                        )}
                        <Text fontSize="md" fontWeight="semibold" lineClamp={1}>
                          {pillar.name}
                        </Text>
                        <Badge>{pillar.commitments.length}</Badge>
                        {editMode && (
                          <>
                            <Spacer />
                            <Icon size={'sm'} cursor={'pointer'}>
                              <MdEdit />
                            </Icon>
                            <PillarDeleteDialog pillarId={pillar.id} pillarName={pillar.name ?? ''}>
                              <Icon size={'sm'} color="red.500" cursor={'pointer'}>
                                <MdDelete />
                              </Icon>
                            </PillarDeleteDialog>
                          </>
                        )}
                      </HStack>

                      <Droppable droppableId={pillar.id} type="commitment" isDropDisabled={!editMode}>
                        {(provided, snapshot) => (
                          <>
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              bg={stringToColor(pillar.name ?? '') + (snapshot.isDraggingOver ? '.200' : '.100')}
                              borderRadius="md"
                              p={4}
                              w="400px"
                            >
                              <VStack gap={4} align="stretch">
                                {pillar.commitments.map((commitment, index) => (
                                  <Draggable
                                    draggableId={commitment.id}
                                    index={index}
                                    key={commitment.id}
                                    isDragDisabled={!editMode}
                                  >
                                    {(provided, snapshot) => (
                                      <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        p={3}
                                        borderWidth="1px"
                                        borderRadius="md"
                                        bg="white"
                                        boxShadow={snapshot.isDragging ? 'lg' : 'sm'}
                                      >
                                        <VStack align="stretch" gap={1}>
                                          <HStack>
                                            {editMode && (
                                              <Icon size={'sm'}>
                                                <MdDragIndicator />
                                              </Icon>
                                            )}
                                            <Text fontWeight="medium" fontSize="sm">
                                              {commitment.name}
                                            </Text>
                                            {editMode && (
                                              <>
                                                <Spacer />
                                                <Icon size={'sm'} cursor={'pointer'}>
                                                  <MdEdit />
                                                </Icon>
                                                <CommitmentDeleteDialog
                                                  commitmentId={commitment.id}
                                                  commitmentName={commitment.name ?? ''}
                                                >
                                                  <Icon size={'sm'} color="red.500" cursor={'pointer'}>
                                                    <MdDelete />
                                                  </Icon>
                                                </CommitmentDeleteDialog>
                                              </>
                                            )}
                                          </HStack>
                                          <Tabs.Root
                                            size={'sm'}
                                            lazyMount
                                            unmountOnExit
                                            defaultValue="objectives"
                                            variant={'enclosed'}
                                          >
                                            <Tabs.List
                                              style={{
                                                display: 'flex',
                                                gap: '0.5rem',
                                                marginTop: '0.5rem',
                                              }}
                                            >
                                              <Tabs.Trigger value="objectives" height={8} flex="1 1 0">
                                                <HStack fontWeight="semibold">
                                                  <FaBullseye size={16} />
                                                  <Text fontSize={'xs'}>Objectives</Text>
                                                </HStack>
                                              </Tabs.Trigger>
                                              <Tabs.Trigger value="indicators" height={8} flex="1 1 0">
                                                <HStack fontWeight="semibold">
                                                  <AiOutlineBarChart size={16} />
                                                  <Text fontSize={'xs'}>Indicateurs</Text>
                                                </HStack>
                                              </Tabs.Trigger>
                                              <Tabs.Trigger value="actions" height={8} flex="1 1 0">
                                                <HStack fontWeight="semibold">
                                                  <LuGrid3X3 size={16} />
                                                  <Text fontSize={'xs'}>Actions</Text>
                                                </HStack>
                                              </Tabs.Trigger>
                                            </Tabs.List>

                                            <Tabs.Content value="objectives">
                                              <CommitmentGoals
                                                slug={slug}
                                                commitment_id={commitment.id}
                                                editMode={editMode}
                                              />
                                            </Tabs.Content>

                                            <Tabs.Content value="indicators">
                                              <CommitmentIndicators
                                                slug={slug}
                                                commitment_id={commitment.id}
                                                editMode={editMode}
                                              />
                                            </Tabs.Content>

                                            <Tabs.Content value="actions">
                                              <CommitmentActions
                                                slug={slug}
                                                commitment_id={commitment.id}
                                                editMode={editMode}
                                              />
                                            </Tabs.Content>
                                          </Tabs.Root>
                                        </VStack>
                                      </Box>
                                    )}
                                  </Draggable>
                                ))}
                              </VStack>
                              {provided.placeholder}
                            </Box>
                            {editMode && (
                              <Box px={4} pb={4} mt={-2}>
                                <CommitmentAddDialog
                                  slug={slug}
                                  pillarId={pillar.id}
                                  order={pillar.commitments.length ?? 0}
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </Droppable>
                    </VStack>
                  )}
                </Draggable>
              ))}
              {editMode && <PillarAddDialog slug={slug} order={csrPlan?.pillars.length ?? 0} />}

              {provided.placeholder}
            </HStack>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}
